import React, { useState, useEffect } from 'react';
import styles from '../styles/pages/Projects.module.css';
import { Project, ProjectsTableOfContents } from '../components';
import { fetchProjects } from '../utils/projectUtils';

/**
 * Projects page component displaying a collection of personal and professional projects
 * Uses the reusable Project component to maintain consistent styling
 * Projects are loaded from JSON files in the public/projects directory
 */
export function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch projects when component mounts
    const loadProjects = async () => {
      try {
        setLoading(true);
        const projectData = await fetchProjects();
        setProjects(projectData);
        setError(null);
      } catch (err) {
        console.error('Failed to load projects:', err);
        setError('Failed to load projects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  return (
    <div className={styles.projectsContainer}>
      <div className={styles.projectsLayout}>
        <div className={styles.mainContent}>
          <h1 className={styles.pageTitle}>My Projects</h1>
          <p className={styles.pageDescription}>
            Here are some of the projects I've worked on in my personal and professional capacities. All work represented here is my own unless otherwise noted.
          </p>
          
          {/* Show loading indicator */}
          {loading && (
            <div className={styles.loadingState}>
              Loading projects...
            </div>
          )}
          
          {/* Show error message if fetching failed */}
          {error && !loading && (
            <div className={styles.errorState}>
              {error}
            </div>
          )}
          
          {/* Display projects if available and not loading */}
          {!loading && !error && (
            <div className={styles.projectsList}>
              {projects.length > 0 ? (
                projects.map(project => (
                  <Project 
                    key={project.id}
                    id={`project-${project.id}`}
                    title={project.title}
                    description={project.description}
                    technologies={project.technologies || []}
                    imageUrl={project.imageUrl}
                    githubUrl={project.codeUrl}
                    liveUrl={project.demoUrl}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  No projects available at the moment. Check back soon!
                </div>
              )}
            </div>
          )}
        </div>
        
        {projects.length > 0 && <ProjectsTableOfContents projects={projects} />}
      </div>
    </div>
  );
}
